import { Global } from '@emotion/react'
import { yupResolver } from '@hookform/resolvers/yup'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import Spacings from 'figma/tokens/Spacings'
import type { Countries } from 'flamingo/config/ConfigCountries'
import { Currencies } from 'flamingo/config/ConfigCurrencies'
import useConfigByCountry from 'flamingo/hooks/useConfigByCountry'
import useLanguage from 'flamingo/hooks/useLanguage'
import { YYYY_MM_DD } from 'mynt-components/Constants'
import FigmaBox from 'mynt-components/components/FigmaBox'
import FigmaButton from 'mynt-components/components/FigmaButton'
import ModalExperimental from 'mynt-components/components/ModalExperimental'
import ModalExperimentalFooter from 'mynt-components/components/ModalExperimentalFooter'
import ModalExperimentalHeader from 'mynt-components/components/ModalExperimentalHeader'
import type React from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { getText, TextKeys } from 'tiger/libs/TextRepository'
import * as Yup from 'yup'
import { DatePickerControllerV2, SelectController, TextFieldController } from '../../../components/react-hook-components'
import TextContainer from '../../../components/TextContainer'

type FormProps = {
  customerId: string
  ocr: string | undefined
  amount: number
  paymentDate: string
  description: string
  currency: Currencies
}

type Props = {
  isOpen: boolean
  customerId: string
  onClose: () => void
  onSubmit: (values: FormProps) => void | Promise<any>
  customerCountry: Countries
}

const ExternalPaymentModal: React.FC<Props> = ({ customerId, onClose, onSubmit, customerCountry }) => {
  const language = useLanguage()
  const countryConfig = useConfigByCountry(customerCountry)
  const [isLoading, setIsLoading] = useState(false)

  const initialValues: FormProps = {
    customerId: customerId,
    ocr: '',
    amount: 0,
    paymentDate: dayjs().format(YYYY_MM_DD),
    description: '',
    currency: countryConfig.currency,
  }

  const validationSchema = Yup.object().shape(
    {
      customerId: Yup.string().when('ocr', {
        is: (ocr: string | undefined) => !ocr,
        // biome-ignore lint/suspicious/noThenProperty: yup's conditional schema API uses a `then` key
        then: (schema) =>
          schema
            .typeError(getText(TextKeys.mFRegisterPaymentCustomerIDOrOCRError, language))
            .required(getText(TextKeys.mFRegisterPaymentCustomerIDOrOCRError, language)),
      }),
      ocr: Yup.string().when('customerId', {
        is: (customerId: string | undefined) => !customerId,
        // biome-ignore lint/suspicious/noThenProperty: yup's conditional schema API uses a `then` key
        then: (schema) =>
          schema
            .typeError(getText(TextKeys.mFRegisterPaymentCustomerIDOrOCRError, language))
            .required(getText(TextKeys.mFRegisterPaymentCustomerIDOrOCRError, language)),
      }),
      amount: Yup.number()
        .typeError(getText(TextKeys.mFRegisterPaymentAmountError, language))
        .required(getText(TextKeys.mFRegisterPaymentAmountError, language)),
      paymentDate: Yup.mixed()
        .typeError(getText(TextKeys.mFRegisterPaymentDateError, language))
        .required(getText(TextKeys.mFRegisterPaymentDateError, language)),
      description: Yup.string()
        .typeError(getText(TextKeys.mFRegisterPaymentDecriptionError, language))
        .required(getText(TextKeys.mFRegisterPaymentDecriptionError, language)),
    },
    [['customerId', 'ocr']],
  )

  const { control, handleSubmit, watch } = useForm<FormProps>({ defaultValues: initialValues, resolver: yupResolver(validationSchema) })

  const submit = async (values: FormProps) => {
    setIsLoading(true)

    const promiseOrVoid = onSubmit(values)

    if (promiseOrVoid instanceof Promise) {
      return promiseOrVoid.finally(() => {
        onClose()
        setIsLoading(false)
      })
    }

    setIsLoading(false)
    onClose()
  }

  return (
    <ModalExperimental
      onClose={onClose}
      backOfficeStyling
      heading={() => (
        <ModalExperimentalHeader>
          <TextContainer textKey={TextKeys.registerPaymentHeading} />
        </ModalExperimentalHeader>
      )}
    >
      <form name="modal_register_external_payment" onSubmit={handleSubmit(submit)}>
        <Global
          styles={`
                    // Fixes the select menu being hidden behind the modal
                    div[role="presentation"] {
                      z-index: 1300 !important;
                    }
                  `}
        />
        <FigmaBox
          fullWidth
          direction="column"
          justify="space-between"
          align="flex-start"
          gap={Spacings.large}
          top={Spacings.large}
          bottom={Spacings.large}
          left={Spacings.medium}
          right={Spacings.medium}
        >
          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentCustomerID} />
            </FigmaBox>
            <TextFieldController
              control={control}
              disabled={!!customerId}
              name="customerId"
              labelTextKey={TextKeys.mFRegisterPaymentCustomerIDrLabel}
            />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentOCR} />
            </FigmaBox>
            <TextFieldController control={control} name="ocr" labelTextKey={TextKeys.mFRegisterPaymentOCRLabel} />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentAmount} text="Currency" />
            </FigmaBox>
            <SelectController
              control={control}
              name="currency"
              options={[
                { value: Currencies.SEK, label: Currencies.SEK },
                { value: Currencies.EUR, label: Currencies.EUR },
                { value: Currencies.DKK, label: Currencies.DKK },
                { value: Currencies.NOK, label: Currencies.NOK },
              ]}
            />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentAmount} />
            </FigmaBox>
            <TextFieldController
              control={control}
              name="amount"
              type="number"
              noLabel
              adornment={<Typography variant="body2">{watch('currency')}</Typography>}
            />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentDate} />
            </FigmaBox>
            <DatePickerControllerV2 control={control} name="paymentDate" labelTextKey={TextKeys.registerPaymentDate} />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="center" justify="space-between">
            <FigmaBox fullWidth right spacing={Spacings.large}>
              <TextContainer textKey={TextKeys.registerPaymentDecription} />
            </FigmaBox>
            <TextFieldController control={control} name="description" labelTextKey={TextKeys.mFRegisterPaymentDecriptionLabel} />
          </FigmaBox>
        </FigmaBox>

        <ModalExperimentalFooter>
          <FigmaBox direction="row" fullWidth align="flex-start" justify="flex-end" gap={Spacings.tiny}>
            <FigmaButton fullWidth={false} variant="tertiary" textKey={TextKeys.createYearNameButtonCancel} onClick={onClose} />
            <FigmaButton fullWidth={false} textKey={TextKeys.registerPaymentButton} loading={isLoading} />
          </FigmaBox>
        </ModalExperimentalFooter>
      </form>
    </ModalExperimental>
  )
}

// Mounted only while open, so each opening starts from fresh initial values
export const ExternalPayment: React.FC<Props> = (props) => (props.isOpen ? <ExternalPaymentModal {...props} /> : null)
